// =====================================================
// BED — Automatisations (migration 00179)
// =====================================================
// Règles de relance, journal des envois et clôture d'édition.
//
// Comme `src/lib/bed/admin.ts`, ce module ne rejoue AUCUN contrôle d'accès :
// chaque RPC porte sa propre garde `superadmin`/`admin` côté serveur (42501
// sinon). Il traduit les erreurs nommées en statut discriminé.

import { supabase } from '../supabase';

// -----------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------

export type BedAutomationKind = 'event' | 'reminder';
export type BedAutomationChannel = 'email' | 'sms';

export interface BedAutomationRule {
  id: string;
  key: string;
  kind: BedAutomationKind;
  trigger_key: string;
  label: string;
  description: string | null;
  channel: BedAutomationChannel;
  email_template_id: string | null;
  email_template_name: string | null;
  /** J+X pour les relances, J−X pour `before_deadline`. `null` pour un événement. */
  delay_days: number | null;
  min_days_between_sends: number;
  /** `null` = pas de plafond. */
  max_sends: number | null;
  is_active: boolean;
  /** Posée par le cahier des charges : réglable et désactivable, pas supprimable. */
  is_builtin: boolean;
  sort_order: number;
  sent_count: number;
  pending_count: number;
  last_sent_at: string | null;
}

export interface BedAutomationRules {
  rules: BedAutomationRule[];
  /** Nombre de cibles dues à l'instant T, par clé de règle de relance. */
  due_now: Record<string, number>;
}

/** Verdict de l'anti-spam pour une cible donnée. */
export interface BedAutomationGate {
  allowed: boolean;
  reason?: string;
  sent_count?: number;
  max_sends?: number;
  last_sent_at?: string;
  min_days_between_sends?: number;
}

export interface BedReminderTarget {
  rule_key: string;
  trigger_key: string;
  crew_id: string;
  crew_number: string | null;
  crew_name: string | null;
  bed_status: string;
  bed_status_label: string;
  anchor_at: string | null;
  days_since: number | null;
  detail: string | null;
  gate: BedAutomationGate;
}

export interface BedClosureBucket {
  count: number;
  label: string;
  target_status: string;
  sends_email: boolean;
  crews: Array<{ crew_id: string; crew_number: string | null; name: string | null }>;
}

export interface BedClosurePreview {
  product: {
    id: string;
    name: string;
    start_date: string | null;
    end_date: string | null;
    bed_info_deadline: string | null;
  };
  to_cancellation_compliant: BedClosureBucket;
  to_cancellation_non_compliant: BedClosureBucket;
  contacts_to_client: number;
  /** Statuts NON impactés, pour que l'admin voie ce qu'il ne touche pas. */
  untouched: Record<string, number>;
  total_impacted: number;
}

export interface BedClosureResult {
  product_id: string;
  product_name: string;
  cancelled_compliant: number;
  cancelled_non_compliant: number;
  contacts_switched_to_client: number;
  preview_before: BedClosurePreview;
  preview_after: BedClosurePreview;
  closed_at: string;
}

export type BedAutomationError = 'forbidden' | 'not_found' | 'builtin' | 'error';

export type BedAutomationResult<T> =
  | { status: 'ok'; data: T }
  | { status: BedAutomationError; message?: string };

// -----------------------------------------------------------------------
// MAPPING D'ERREURS
// -----------------------------------------------------------------------

function classify(message: string | undefined): BedAutomationError {
  if (!message) return 'error';
  if (message.includes('insufficient_privilege')) return 'forbidden';
  if (message.includes('builtin')) return 'builtin';
  if (message.includes('not_found')) return 'not_found';
  return 'error';
}

export function bedAutomationErrorMessage(error: BedAutomationError): string {
  switch (error) {
    case 'forbidden':
      return "Vous n'avez pas les droits sur les automatisations BED.";
    case 'builtin':
      return 'Cette règle vient du cahier des charges : désactivez-la plutôt que de la modifier.';
    case 'not_found':
      return 'Règle ou produit introuvable.';
    default:
      return 'Une erreur est survenue.';
  }
}

/** Libellé lisible d'un refus de l'anti-spam. */
export function gateReasonLabel(gate: BedAutomationGate): string {
  if (gate.allowed) return 'Partira au prochain passage';
  switch (gate.reason) {
    case 'cooldown':
      return `Déjà relancé il y a moins de ${gate.min_days_between_sends} j`;
    case 'max_sends_reached':
      return `Plafond atteint (${gate.sent_count}/${gate.max_sends})`;
    case 'rule_inactive':
      return 'Règle désactivée';
    case 'no_template':
      return "Aucun modèle d'email associé";
    case 'rule_not_found':
      return 'Règle introuvable';
    default:
      return 'Ne partira pas';
  }
}

// -----------------------------------------------------------------------
// APPELS
// -----------------------------------------------------------------------

export async function getBedAutomationRules(): Promise<BedAutomationResult<BedAutomationRules>> {
  try {
    const { data, error } = await supabase.rpc('get_bed_automation_rules');
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedAutomationRules };
  } catch (err) {
    console.error('[BED-AUTO] get_bed_automation_rules:', err);
    return { status: 'error' };
  }
}

export interface BedRulePatch {
  is_active?: boolean;
  delay_days?: number;
  min_days_between_sends?: number;
  /** `-1` remet le plafond à « illimité ». */
  max_sends?: number;
  channel?: BedAutomationChannel;
  email_template_id?: string;
}

/**
 * Réglage d'une règle. Chaque champ absent est laissé en place par le serveur
 * (`COALESCE`) : on n'envoie que ce que l'admin a changé.
 */
export async function setBedAutomationRule(
  key: string,
  patch: BedRulePatch
): Promise<BedAutomationResult<BedAutomationRules>> {
  try {
    const { data, error } = await supabase.rpc('set_bed_automation_rule', {
      p_key: key,
      p_is_active: patch.is_active ?? null,
      p_delay_days: patch.delay_days ?? null,
      p_min_days_between_sends: patch.min_days_between_sends ?? null,
      p_max_sends: patch.max_sends ?? null,
      p_channel: patch.channel ?? null,
      p_email_template_id: patch.email_template_id ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedAutomationRules };
  } catch (err) {
    console.error('[BED-AUTO] set_bed_automation_rule:', err);
    return { status: 'error' };
  }
}

/** Qui serait relancé, pourquoi, et ce que l'anti-spam laisserait partir. */
export async function getBedReminderPreview(): Promise<BedAutomationResult<BedReminderTarget[]>> {
  try {
    const { data, error } = await supabase.rpc('get_bed_reminder_preview');
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: (data as { targets: BedReminderTarget[] }).targets ?? [] };
  } catch (err) {
    console.error('[BED-AUTO] get_bed_reminder_preview:', err);
    return { status: 'error' };
  }
}

export async function previewBedEditionClosure(
  productId: string
): Promise<BedAutomationResult<BedClosurePreview>> {
  try {
    const { data, error } = await supabase.rpc('bed_close_edition_preview', {
      p_product_id: productId,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedClosurePreview };
  } catch (err) {
    console.error('[BED-AUTO] bed_close_edition_preview:', err);
    return { status: 'error' };
  }
}

export async function executeBedEditionClosure(
  productId: string
): Promise<BedAutomationResult<BedClosureResult>> {
  try {
    const { data, error } = await supabase.rpc('bed_close_edition_execute', {
      p_product_id: productId,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedClosureResult };
  } catch (err) {
    console.error('[BED-AUTO] bed_close_edition_execute:', err);
    return { status: 'error' };
  }
}

export interface BedAutomationRunSummary {
  ok: boolean;
  mode: string;
  dry_run: boolean;
  reminders: {
    selected: number;
    enqueued: number;
    skipped: number;
    by_rule: Record<string, number>;
    skip_reasons: Record<string, number>;
  } | null;
  queue: {
    processed: number;
    sent: number;
    failed: number;
    skipped: number;
    results: Array<Record<string, unknown>>;
  };
}

/**
 * Déclenche le worker à la main (« Exécuter maintenant » / « Simuler »).
 * L'Edge Function accepte le JWT d'un admin en plus du secret du cron.
 */
export async function runBedAutomations(
  mode: 'run' | 'process' | 'preview'
): Promise<BedAutomationResult<BedAutomationRunSummary>> {
  try {
    const { data, error } = await supabase.functions.invoke('bed-automations', {
      body: { mode },
    });
    if (error) return { status: 'error', message: error.message };
    return { status: 'ok', data: data as BedAutomationRunSummary };
  } catch (err) {
    console.error('[BED-AUTO] invoke bed-automations:', err);
    return { status: 'error', message: err instanceof Error ? err.message : undefined };
  }
}
